import type { EditorView } from "prosemirror-view";
import type { Command, EditorState } from "prosemirror-state";
import type { MarkType, NodeType, Attrs } from "prosemirror-model";
import { toggleMark, setBlockType, wrapIn } from "prosemirror-commands";
import { sinkListItem, liftListItem } from "prosemirror-schema-list";
import { schema } from "./schema";
import type { ListStyle, Msg } from "../types";
import { defaultViewBlockForm } from "./view-block";
import { findLinkAround } from "./link";

type ToolbarAction = {
  label: string;
  title: string;
  // `cardId` is the card of `view`, which can be the card of a section.
  command: (view: EditorView, cardId: string, dispatch: (msg: Msg) => void) => void;
  // Whether the button should render as toggled for the current selection.
  active?: (state: EditorState) => boolean;
  // Whether the command can run. The button is disabled when it cannot.
  enabled?: (state: EditorState) => boolean;
};

// True if `type` is applied across the whole selection (or stored for the
// cursor when the selection is empty).
function markActive(state: EditorState, type: MarkType): boolean {
  const { from, to, empty, $from } = state.selection;
  if (empty) return !!type.isInSet(state.storedMarks || $from.marks());
  return state.doc.rangeHasMark(from, to, type);
}

// True if the selection's textblock is exactly `type` (+ optional attrs).
function blockActive(state: EditorState, type: NodeType, attrs?: Attrs): boolean {
  const { $from, to } = state.selection;
  return to <= $from.end() && $from.parent.hasMarkup(type, attrs);
}

// True if the selection sits inside a list of the given `style`.
function listActive(state: EditorState, style: string): boolean {
  const { $from } = state.selection;
  for (let d = $from.depth; d > 0; d--) {
    const node = $from.node(d);
    if (node.type === schema.nodes["list"] && node.attrs["style"] === style) return true;
  }
  return false;
}

// Apply a list of `style` to the selection. If already inside a list, switch
// that list's flavour in place (so bullet↔ordered↔todo toggling works);
// otherwise wrap the selection in a new list.
function setList(style: ListStyle): Command {
  return (state, dispatch) => {
    const list = schema.nodes["list"]!;
    const { $from } = state.selection;
    for (let d = $from.depth; d > 0; d--) {
      const node = $from.node(d);
      if (node.type === list) {
        // Same flavour already active → toggle the list off (lift item out).
        if (node.attrs["style"] === style) {
          return liftListItem(schema.nodes["list_item"]!)(state, dispatch);
        }
        if (dispatch) {
          dispatch(state.tr.setNodeMarkup($from.before(d), undefined, { ...node.attrs, style }));
        }
        return true;
      }
    }
    return wrapIn(list, { style })(state, dispatch);
  };
}

// Set the block to `type`, or back to a paragraph if it already has that type
// (so the button toggles off).
function toggleBlock(type: NodeType, attrs?: Attrs): Command {
  return (state, dispatch) => {
    const cmd = blockActive(state, type, attrs)
      ? setBlockType(schema.nodes["paragraph"]!)
      : setBlockType(type, attrs);
    return cmd(state, dispatch);
  };
}

// Run `cmd` from a toolbar button, then give the focus back to the editor.
function run(cmd: Command): (view: EditorView) => void {
  return (v) => {
    cmd(v.state, v.dispatch);
    v.focus();
  };
}

// Each factory builds the command once, so the button reports the state of the
// command that the click runs.
function markAction(name: string, label: string, title: string): ToolbarAction {
  const type = schema.marks[name]!;
  const cmd = toggleMark(type);
  return { label, title, command: run(cmd), active: (s) => markActive(s, type), enabled: cmd };
}

function headingAction(level: number): ToolbarAction {
  const cmd = toggleBlock(schema.nodes["heading"]!, { level });
  return {
    label: `H${level}`,
    title: `Heading ${level}`,
    command: run(cmd),
    active: (s) => blockActive(s, schema.nodes["heading"]!, { level }),
    enabled: cmd,
  };
}

function listAction(style: ListStyle, label: string, title: string): ToolbarAction {
  const cmd = setList(style);
  return { label, title, command: run(cmd), active: (s) => listActive(s, style), enabled: cmd };
}

function listItemAction(cmd: Command, label: string, title: string): ToolbarAction {
  return { label, title, command: run(cmd), enabled: cmd };
}

const codeBlock = toggleBlock(schema.nodes["code_block"]!);

export const actions: ToolbarAction[] = [
  markAction("bold", "B", "Bold (Ctrl+B)"),
  markAction("italic", "I", "Italic (Ctrl+I)"),
  markAction("strikethrough", "S̶", "Strikethrough"),
  markAction("superscript", "x²", "Superscript"),
  markAction("subscript", "x₂", "Subscript"),
  markAction("code", "`", "Inline code"),
  {
    label: "🔗",
    title: "Insert / edit link",
    command: (v, cardId, dispatch) => {
      const sel = v.state.selection;
      // Cursor inside a link (no selection) → edit it in place; otherwise link
      // the current selection (or insert at the cursor).
      const existing = sel.empty ? findLinkAround(v.state, sel.from) : null;
      const from = existing ? existing.from : sel.from;
      const to = existing ? existing.to : sel.to;
      const text = from === to ? "" : v.state.doc.textBetween(from, to, " ");
      const url = existing ? existing.href : "";
      dispatch({ type: "OpenLinkDialog", dialog: { cardId, text, url, from, to } });
    },
    active: (s) => markActive(s, schema.marks["link"]!),
    enabled: toggleMark(schema.marks["link"]!),
  },
  headingAction(1),
  headingAction(2),
  headingAction(3),
  listAction("bullet", "• list", "Bullet list"),
  listAction("ordered", "1. list", "Ordered list"),
  listAction("todo", "☐ list", "Todo list"),
  listItemAction(sinkListItem(schema.nodes["list_item"]!), "⇥", "Nest list item (Tab)"),
  listItemAction(liftListItem(schema.nodes["list_item"]!), "⇤", "Unnest list item (Shift+Tab)"),
  {
    label: "```",
    title: "Code block",
    command: run(codeBlock),
    active: (s) => blockActive(s, schema.nodes["code_block"]!),
    enabled: codeBlock,
  },
  {
    label: "⊞ View",
    title: "Insert view block",
    command: (_v, cardId, dispatch) => {
      dispatch({
        type: "OpenViewBlockDialog",
        dialog: { cardId, mode: "insert", pos: null, form: defaultViewBlockForm() },
      });
    },
  },
];
